import type { InputHTMLAttributes } from "react";
import moment from "moment";

const TIME_PARSE_FORMATS = ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"];

/** Normalize API / DB times to HH:mm for HTML type="time" inputs. */
export function normalizeTimeForInput(
  time: string | number | null | undefined
): string {
  if (time === null || time === undefined || String(time).trim() === "") {
    return "";
  }
  const parsed = moment(String(time), TIME_PARSE_FORMATS, true);
  return parsed.isValid() ? parsed.format("HH:mm") : "";
}

/** Parse manually typed times (e.g. 8:30, 0830) into HH:mm for time inputs. */
export function parseFlexibleTimeInput(raw: string | null | undefined): string {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return "";

  const normalized = normalizeTimeForInput(trimmed);
  if (normalized) return normalized;

  if (/^\d{3,4}$/.test(trimmed)) {
    const padded = trimmed.padStart(4, "0");
    const hours = parseInt(padded.slice(0, 2), 10);
    const minutes = parseInt(padded.slice(2), 10);
    if (hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }
  }

  const parts = trimmed.split(/[:.\-]/);
  if (parts.length === 2) {
    const hours = parseInt(parts[0], 10);
    const minutes = parseInt(parts[1], 10);
    if (
      !Number.isNaN(hours) &&
      !Number.isNaN(minutes) &&
      hours >= 0 &&
      hours < 24 &&
      minutes >= 0 &&
      minutes < 60
    ) {
      return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
    }
  }

  return "";
}

const timeStringToMinutes = (time: string): number => {
  const normalized = normalizeTimeForInput(time);
  if (!normalized) return 0;
  const parsed = moment(normalized, TIME_PARSE_FORMATS, true);
  if (parsed.isValid()) {
    return parsed.hours() * 60 + parsed.minutes();
  }
  const [hours, minutes] = normalized.split(":").map(Number);
  return (hours || 0) * 60 + (minutes || 0);
};

const minutesToTimeString = (totalMinutes: number): string => {
  const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours = Math.floor(normalized / 60);
  const mins = normalized % 60;
  return `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}`;
};

/** Cursor position in "HH:mm" — positions 0-2 are hours, 3+ are minutes. */
export function getTimeSegmentFromCursor(
  cursorPos: number | null | undefined
): "hour" | "minute" {
  if (cursorPos === null || cursorPos === undefined) {
    return "hour";
  }
  return cursorPos <= 2 ? "hour" : "minute";
}

/** Step time up/down; segment picks hour (+/- 1 h) vs minute (+/- 1 min). */
export function adjustTimeWithArrowKey(
  currentValue: string,
  key: "ArrowUp" | "ArrowDown",
  stepMinutes = 1,
  minTime?: string,
  maxTime?: string,
  segment: "hour" | "minute" = "minute"
): string {
  const minMinutes = minTime ? timeStringToMinutes(minTime) : 0;
  const maxMinutes = maxTime ? timeStringToMinutes(maxTime) : 24 * 60 - 1;
  const effectiveStep = segment === "hour" ? 60 : stepMinutes;
  const delta = key === "ArrowUp" ? effectiveStep : -effectiveStep;

  let nextMinutes = timeStringToMinutes(currentValue);
  if (!normalizeTimeForInput(currentValue)) {
    nextMinutes = minTime ? minMinutes : 0;
  } else {
    nextMinutes += delta;
  }

  if (minTime && nextMinutes < minMinutes) nextMinutes = minMinutes;
  if (maxTime && nextMinutes > maxMinutes) nextMinutes = maxMinutes;

  return minutesToTimeString(nextMinutes);
}

export type TimeKeyboardInputProps = Pick<
  InputHTMLAttributes<HTMLInputElement>,
  "onKeyDown" | "onFocus" | "onClick" | "onBlur"
>;

/** Arrow-key hour/minute stepping for HH:mm inputs (MUI KeyboardTimePicker or text). */
export function getTimeKeyboardInputProps(options: {
  timeValue: string;
  disabled?: boolean;
  onTimeChange: (value: string) => void;
  minTime?: string;
  maxTime?: string;
}): TimeKeyboardInputProps {
  const { timeValue, disabled, onTimeChange, minTime, maxTime } = options;

  const focusTimePart = (input: HTMLInputElement, segment: "hour" | "minute") => {
    const display = normalizeTimeForInput(input.value) || timeValue || "00:00";
    try {
      if (segment === "hour") {
        input.setSelectionRange(0, 2);
      } else {
        input.setSelectionRange(3, display.length);
      }
    } catch {
      // ignore
    }
  };

  const syncTimeSelection = (input: HTMLInputElement) => {
    requestAnimationFrame(() => {
      const segment = getTimeSegmentFromCursor(input.selectionStart);
      focusTimePart(input, segment);
    });
  };

  return {
    onClick: (event) => {
      if (disabled) return;
      syncTimeSelection(event.currentTarget);
    },
    onFocus: (event) => {
      if (disabled) return;
      syncTimeSelection(event.currentTarget);
    },
    onKeyDown: (event) => {
      if (disabled) return;

      const input = event.currentTarget;

      if (event.key === "ArrowLeft") {
        event.preventDefault();
        focusTimePart(input, "hour");
        return;
      }
      if (event.key === "ArrowRight") {
        event.preventDefault();
        focusTimePart(input, "minute");
        return;
      }
      if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;

      event.preventDefault();
      event.stopPropagation();

      const segment = getTimeSegmentFromCursor(input.selectionStart);
      const currentVal = normalizeTimeForInput(input.value || timeValue || "");
      const newValue = adjustTimeWithArrowKey(
        currentVal,
        event.key,
        1,
        minTime,
        maxTime,
        segment
      );

      if (!newValue || newValue === currentVal) return;

      onTimeChange(newValue);
      requestAnimationFrame(() => focusTimePart(input, segment));
    },
    onBlur: (event) => {
      if (disabled) return;
      const parsed = parseFlexibleTimeInput(event.currentTarget.value);
      if (!parsed || parsed === event.currentTarget.value) return;
      onTimeChange(parsed);
    },
  };
}
